/**
 * Checkers UI (Phase 2). Renders the board + practice page + entry
 * modal. All engine logic lives server-side; this file only handles
 * click → dispatch → animate.
 *
 * Board conventions:
 *   - Rows indexed 0..7 top → bottom. `state.board[r][c]` is '', 'b',
 *     'B', 'r', 'R'.
 *   - `state.legal_moves` is server-generated and authoritative.
 *   - `color_map` (live) maps user.id → 'black'|'red'. Practice
 *     hard-codes the human as black.
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from './components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './components/ui/dialog';
import { Input } from './components/ui/input';
import { Trophy, Crown, Zap, ChevronRight, ArrowLeft } from 'lucide-react';

const SIZE = 8;
const posKey = (r, c) => `${r},${c}`;
const arrEq = (a, b) => a && b && a.length === b.length && a.every((v, i) => v === b[i]);

// ------------------------------------------------------------------
// <CheckersBoard>  — pure render + click dispatch. Parent owns state.
// ------------------------------------------------------------------
export const CheckersBoard = ({
  state,
  myColor,
  onMove,
  onHuff,
  disabled = false,
}) => {
  const [selected, setSelected] = useState(null);   // [r,c] or null
  const [huffing, setHuffing] = useState(false);    // huff-mode toggle
  const legalMoves = state?.legal_moves || [];
  const huffable = state?.huffable || [];
  const turn = state?.turn;
  const isMyTurn = myColor && turn === myColor && !state?.winner && !state?.draw;

  // Legal destinations for the currently-selected piece.
  const legalForSelected = useMemo(() => {
    if (!selected) return [];
    return legalMoves.filter(m => arrEq(m.from, selected));
  }, [selected, legalMoves]);

  useEffect(() => { setSelected(null); setHuffing(false); }, [state?.turn]);

  const canHuff = state?.config?.capture_rule === 'huffing'
    && huffable.length > 0
    && isMyTurn;

  const onCell = (r, c) => {
    if (disabled || !isMyTurn) return;
    const piece = state.board[r][c];
    if (huffing) {
      // Click an opponent huffable piece to remove it.
      if (huffable.some(p => arrEq(p, [r, c]))) {
        onHuff && onHuff([r, c]);
        setHuffing(false);
      }
      return;
    }
    // Clicking own piece with legal moves → select.
    if (piece && piece.toLowerCase() === myColor[0]
        && legalMoves.some(m => arrEq(m.from, [r, c]))) {
      setSelected([r, c]);
      return;
    }
    // Clicking a legal destination for current selection → move.
    if (selected) {
      const cand = legalForSelected.filter(m => arrEq(m.to, [r, c]));
      if (cand.length) {
        // If multiple moves land here (rare), pick the longest capture chain.
        cand.sort((a, b) => (b.captured?.length || 0) - (a.captured?.length || 0));
        onMove && onMove(cand[0]);
        setSelected(null);
      }
    }
  };

  const legalDestSet = new Set(legalForSelected.map(m => posKey(m.to[0], m.to[1])));
  const huffableSet = new Set(huffable.map(p => posKey(p[0], p[1])));
  const selKey = selected ? posKey(selected[0], selected[1]) : null;
  // Always show the human's own pieces at the BOTTOM (standard checkers
  // orientation). Practice human is black, whose pieces start on the top
  // rows — so render a 180° view for black. Board coordinates and every
  // click handler stay in true board space; only the on-screen order is
  // mapped through view().
  const flip = myColor === 'black';
  const view = (i) => (flip ? SIZE - 1 - i : i);
  // Pieces that have at least one legal move this turn — highlighted so
  // it's obvious what you can tap (fixes "I clicked and nothing moved").
  const movableSet = new Set(
    (isMyTurn && !huffing) ? legalMoves.map(m => posKey(m.from[0], m.from[1])) : []
  );

  return (
    <div className="flex flex-col items-center gap-3 select-none" data-testid="checkers-board-container">
      {/* Turn indicator */}
      <div className="flex items-center gap-3 text-sm">
        <span className={`px-3 py-1 rounded-full font-bold ${isMyTurn ? 'bg-emerald-500/20 text-emerald-300 ring-1 ring-emerald-500/60' : 'bg-white/5 text-white/60'}`} data-testid="checkers-turn-indicator">
          {state?.winner
            ? (state.winner === myColor ? 'You win!' : 'You lose')
            : state?.draw
              ? 'Draw'
              : isMyTurn ? 'Your turn' : `${turn === 'black' ? 'Black' : 'Red'}'s turn`}
        </span>
        {canHuff && (
          <Button
            size="sm"
            variant={huffing ? 'default' : 'outline'}
            onClick={() => setHuffing(v => !v)}
            className={huffing ? 'bg-amber-500 hover:bg-amber-400 text-black' : 'border-amber-500/60 text-amber-300 hover:bg-amber-500/20'}
            data-testid="checkers-huff-btn"
          >
            <Zap className="w-3 h-3 mr-1" /> {huffing ? 'Cancel huff' : 'Huff a piece'}
          </Button>
        )}
      </div>

      {/* Board — 8x8 dark wood grid */}
      <div
        /* SEP 6 2026: official-art aesthetic — dark emerald board, gold frame */
        className="grid rounded-xl p-2 shadow-2xl shadow-black/70 ring-4 ring-yellow-700/70 bg-gradient-to-br from-emerald-950 via-black to-emerald-950"
        style={{ gridTemplateColumns: `repeat(${SIZE}, minmax(0, 1fr))`, width: 'min(88vw, 520px)' }}
        data-testid="checkers-board-grid"
      >
        {Array.from({ length: SIZE }).map((_, dr) =>
          Array.from({ length: SIZE }).map((_, dc) => {
            const r = view(dr);
            const c = view(dc);
            const dark = (r + c) % 2 === 1;
            const piece = state?.board?.[r]?.[c] || '';
            const isSel = selKey === posKey(r, c);
            const isDest = legalDestSet.has(posKey(r, c));
            const isHuffTarget = huffing && huffableSet.has(posKey(r, c));
            const isMovable = movableSet.has(posKey(r, c)) && !isSel;
            return (
              <div
                key={`${r}-${c}`}
                onClick={() => onCell(r, c)}
                className={[
                  'aspect-square relative flex items-center justify-center transition-all duration-150',
                  dark
                    ? 'bg-gradient-to-br from-emerald-950 to-black cursor-pointer'
                    : 'bg-gradient-to-br from-emerald-800/60 to-emerald-900/60',
                  isSel ? 'ring-4 ring-emerald-400 ring-inset' : '',
                  isDest ? 'ring-2 ring-emerald-300/70 ring-inset' : '',
                  isHuffTarget ? 'ring-4 ring-amber-400 ring-inset' : '',
                  isMovable ? 'ring-2 ring-emerald-400/40 ring-inset' : '',
                ].join(' ')}
                data-testid={`checkers-cell-${r}-${c}`}
              >
                {isDest && !piece && (
                  <span className="absolute w-3 h-3 rounded-full bg-emerald-300/70 animate-pulse" />
                )}
                {piece && (
                  <div
                    className={[
                      'w-[78%] h-[78%] rounded-full flex items-center justify-center transition-all duration-200',
                      piece.toLowerCase() === 'b'
                        ? 'bg-gradient-to-br from-slate-800 to-black shadow-lg shadow-black/60 ring-2 ring-slate-500/40'
                        : 'bg-gradient-to-br from-red-500 to-red-800 shadow-lg shadow-red-900/60 ring-2 ring-red-300/40',
                      isSel ? 'scale-110' : '',
                    ].join(' ')}
                    data-testid={`checkers-piece-${r}-${c}`}
                  >
                    {piece === piece.toUpperCase() && (
                      <Crown className={`w-1/2 h-1/2 ${piece === 'B' ? 'text-amber-300' : 'text-amber-200'}`} />
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      <div className="text-xs text-white/40 mt-1">
        {state?.config?.capture_rule === 'compel' ? 'Compel' : 'Huffing'} · Pool {state?.config?.pool_rules ? 'ON' : 'OFF'}
      </div>
    </div>
  );
};

// ------------------------------------------------------------------
// <CheckersPracticePage> — server-authoritative practice vs AI.
// ------------------------------------------------------------------
export const CheckersPracticePage = ({ api, user, setUser }) => {
  const navigate = useNavigate();
  const [state, setState] = useState(null);
  const [aiThinking, setAiThinking] = useState(false);
  const [difficulty] = useState(() => {
    try {
      return localStorage.getItem('gems_checkers_ai') || 'medium';
    } catch { return 'medium'; }
  });
  const [config] = useState(() => {
    try {
      const raw = localStorage.getItem('gems_checkers_cfg');
      if (raw) return JSON.parse(raw);
    } catch { /* ignore */ }
    return { capture_rule: 'huffing', pool_rules: true };
  });

  const start = useCallback(async () => {
    try {
      const res = await api.post('/checkers/practice/new', { config });
      setState(res.data);
    } catch (e) {
      toast.error('Failed to start practice');
    }
  }, [api, config]);

  useEffect(() => { start(); }, [start]);

  // When it's AI's turn (red), request a move.
  useEffect(() => {
    if (!state || !state._internal) return;
    if (state.winner || state.draw) return;
    if (state.turn !== 'red' || aiThinking) return;
    setAiThinking(true);
    const t = setTimeout(async () => {
      try {
        const res = await api.post('/checkers/practice/ai', {
          state: state._internal,
          difficulty,
        });
        setState(res.data);
      } catch (e) {
        toast.error('AI move failed');
      } finally {
        setAiThinking(false);
      }
    }, 650);  // watchable delay
    return () => { clearTimeout(t); setAiThinking(false); };
  }, [state?.turn, state?.winner]); // eslint-disable-line

  const applyHumanMove = async (move) => {
    if (!state?._internal) return;
    try {
      const res = await api.post('/checkers/practice/move', {
        state: state._internal, move,
      });
      setState(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Illegal move');
    }
  };

  const applyHuff = async (pos) => {
    if (!state?._internal) return;
    try {
      const res = await api.post('/checkers/practice/huff', {
        state: state._internal, color: 'black', pos,
      });
      setState(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Huff failed');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0B0F1A] via-[#0F1420] to-[#1A0F0F] text-white">
      <div className="max-w-3xl mx-auto p-4">
        <div className="flex items-center justify-between mb-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/games')} data-testid="checkers-practice-back">
            <ArrowLeft className="w-4 h-4 mr-1" /> Games
          </Button>
          <div className="text-sm text-white/60">
            Practice · <span className="capitalize font-bold text-emerald-300">{difficulty}</span> AI
          </div>
        </div>

        {state ? (
          <>
            <CheckersBoard
              state={state}
              myColor="black"
              onMove={applyHumanMove}
              onHuff={applyHuff}
              disabled={aiThinking}
            />
            {(state.winner || state.draw) && (
              <div className="mt-6 text-center" data-testid="checkers-practice-end">
                <div className="inline-flex items-center gap-2 text-2xl font-bold">
                  <Trophy className="w-6 h-6 text-yellow-400" />
                  {state.draw ? "It's a draw" : state.winner === 'black' ? 'You win!' : 'AI wins'}
                </div>
                <div className="mt-3">
                  <Button onClick={start} className="bg-emerald-600 hover:bg-emerald-500 text-white" data-testid="checkers-practice-restart">
                    <ChevronRight className="w-4 h-4 mr-1" /> Play again
                  </Button>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-20 text-white/50">Setting up board…</div>
        )}
      </div>
    </div>
  );
};

// ------------------------------------------------------------------
// <CheckersEntryModal> — rule controls + timer + practice difficulty.
// Persists selections to localStorage so the practice page can read
// them without prop drilling.
// ------------------------------------------------------------------
export const CheckersEntryModal = ({
  open, onOpenChange, mode, wager, setWager,
  minMatchEntry, onStart, userEmeralds = 0,
  hasLinkedOpponent = false,
}) => {
  const [captureRule, setCaptureRule] = useState('huffing');
  const [poolRules, setPoolRules] = useState(true);
  const [menBackward, setMenBackward] = useState(false);
  const [timerOn, setTimerOn] = useState(false);
  const [difficulty, setDifficulty] = useState('medium');

  useEffect(() => {
    if (!open) return;
    try {
      const cfg = JSON.parse(localStorage.getItem('gems_checkers_cfg') || 'null');
      if (cfg) { setCaptureRule(cfg.capture_rule); setPoolRules(cfg.pool_rules); setMenBackward(!!cfg.men_backward_capture); }
      const t = localStorage.getItem('gems_checkers_timer');
      if (t) setTimerOn(t === '1');
      const d = localStorage.getItem('gems_checkers_ai');
      if (d) setDifficulty(d);
    } catch { /* ignore */ }
  }, [open]);

  const persist = () => {
    try {
      localStorage.setItem('gems_checkers_cfg', JSON.stringify({
        capture_rule: captureRule, pool_rules: poolRules,
        men_backward_capture: poolRules ? menBackward : false,
      }));
      localStorage.setItem('gems_checkers_timer', timerOn ? '1' : '0');
      localStorage.setItem('gems_checkers_ai', difficulty);
    } catch { /* ignore */ }
  };

  const isPractice = mode === 'practice';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0F1420] text-white ring-1 ring-white/10 max-w-md" data-testid="checkers-entry-modal">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-center">
            Checkers — {isPractice ? 'Practice vs AI' : 'Quick Match'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Capture Rule selector */}
          <div>
            <div className="text-sm font-medium text-white/80 mb-2">Capture Rule</div>
            <div className="grid grid-cols-2 gap-2">
              {[
                { id: 'huffing', title: 'Huffing', desc: 'Skip a capture — but the opponent can remove that piece and still take their turn.' },
                { id: 'compel',  title: 'Compel',  desc: 'Captures are forced — you must take an available jump.' },
              ].map(o => (
                <button
                  key={o.id}
                  type="button"
                  onClick={() => setCaptureRule(o.id)}
                  className={`text-left p-3 rounded-lg ring-1 transition-all ${captureRule === o.id ? 'bg-emerald-500/15 ring-emerald-500 text-white' : 'bg-white/5 ring-white/10 text-white/70 hover:ring-white/30'}`}
                  data-testid={`checkers-capture-${o.id}`}
                >
                  <div className="text-sm font-bold">{o.title}</div>
                  <div className="text-[11px] opacity-80 mt-1 leading-snug">{o.desc}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Pool Rules toggle + optional men-backward sub-rule.
              Pool ON = kings fly (automatic). "Men can jump backward"
              is a separate optional sub-toggle, default OFF. */}
          <div className="p-3 bg-white/5 rounded-lg space-y-3">
            <label className="flex items-center gap-3 cursor-pointer" data-testid="checkers-pool-toggle">
              <input
                type="checkbox"
                checked={poolRules}
                onChange={(e) => setPoolRules(e.target.checked)}
                className="w-4 h-4 accent-emerald-500"
              />
              <div className="flex-1">
                <div className="text-sm font-bold text-white">Pool Checkers Rules</div>
                <div className="text-[11px] text-white/60 leading-snug">
                  Kings fly — move and capture any distance. Men still move forward only.
                </div>
              </div>
            </label>
            {poolRules && (
              <label className="flex items-center gap-3 cursor-pointer pl-6 border-l-2 border-emerald-500/30 ml-1" data-testid="checkers-men-backward-toggle">
                <input
                  type="checkbox"
                  checked={menBackward}
                  onChange={(e) => setMenBackward(e.target.checked)}
                  className="w-4 h-4 accent-emerald-500"
                />
                <div className="flex-1">
                  <div className="text-sm font-bold text-white">Men can jump backward</div>
                  <div className="text-[11px] text-white/60 leading-snug">
                    Optional — lets men capture backward too. Off = men jump forward only (kings still go any direction).
                  </div>
                </div>
              </label>
            )}
          </div>

          {/* Timer toggle */}
          <label className="flex items-center gap-3 p-3 bg-white/5 rounded-lg cursor-pointer" data-testid="checkers-timer-toggle">
            <input
              type="checkbox"
              checked={timerOn}
              onChange={(e) => setTimerOn(e.target.checked)}
              className="w-4 h-4 accent-emerald-500"
            />
            <div className="flex-1">
              <div className="text-sm font-bold text-white">Per-move Timer</div>
              <div className="text-[11px] text-white/60">Broadcast a move deadline; timeout forfeits.</div>
            </div>
          </label>

          {/* Difficulty (practice only) */}
          {isPractice && (
            <div data-testid="checkers-difficulty-section">
              <div className="text-sm font-medium text-white/80 mb-2">AI Difficulty</div>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { id: 'easy',   title: 'Easy',   desc: 'Casual — great for learning.' },
                  { id: 'medium', title: 'Medium', desc: 'Solid challenge.' },
                  { id: 'hard',   title: 'Hard',   desc: 'Real strategy. Tough.' },
                ].map(d => (
                  <button
                    key={d.id}
                    type="button"
                    onClick={() => setDifficulty(d.id)}
                    className={`text-left p-2 rounded-lg ring-1 transition-all ${difficulty === d.id ? 'bg-emerald-500/15 ring-emerald-500' : 'bg-white/5 ring-white/10 hover:ring-white/30'}`}
                    data-testid={`checkers-diff-${d.id}`}
                  >
                    <div className="text-xs font-bold">{d.title}</div>
                    <div className="text-[10px] opacity-70 mt-0.5 leading-tight">{d.desc}</div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Wager (live only) */}
          {!isPractice && (
            <div>
              <div className="text-sm font-medium text-white/80 mb-2">Match Entry (Emeralds)</div>
              <Input
                type="number"
                value={wager}
                onChange={(e) => setWager(Number(e.target.value) || 0)}
                min={minMatchEntry}
                data-testid="checkers-wager-input"
              />
              <p className="text-xs text-white/50 mt-1">
                Minimum {minMatchEntry} · Your balance: {userEmeralds}
              </p>
            </div>
          )}

          <Button
            onClick={() => {
              persist();
              onStart && onStart({
                capture_rule: captureRule,
                pool_rules: poolRules,
                men_backward_capture: poolRules ? menBackward : false,
                timer_on: timerOn,
                difficulty,
              });
            }}
            className="w-full bg-emerald-600 hover:bg-emerald-500 text-white font-bold"
            data-testid="checkers-entry-start"
          >
            {isPractice
              ? 'Start Practice'
              : hasLinkedOpponent
                ? `Send Challenge${wager > 0 ? ` (${wager})` : ''}`
                : `Go Live${wager > 0 ? ` (${wager})` : ''}`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
